import { AES } from 'crypto-ts';
import * as CryptoTS from 'crypto-ts';

export class EncryptDecryptService {
    public EncryptKey: string = "1~4@60E2#D-C0d8Bf-4B81$aB9b$63@F8*E2&03$F28bB"; // default static encryption key (override with env)

    constructor() {
        try {
            // Use environment-provided key if available (React env must start with REACT_APP_)
            const envKey = (process && (process.env as any)?.REACT_APP_ENCRYPTION_KEY) || (window && (window as any)?.REACT_APP_ENCRYPTION_KEY);
            if (envKey && envKey !== "") this.EncryptKey = envKey;
        } catch (e) {
            // ignore - fallback to default
        }
    }

    encrypt(plainText: any) {
        try {
            if (typeof plainText === "string")
                return AES.encrypt(plainText.trim(), this.EncryptKey).toString();
            else if (typeof plainText === "boolean" || typeof plainText === "number") {
                return AES.encrypt(plainText.toString(), this.EncryptKey).toString();
            } else {
                return AES.encrypt(plainText, this.EncryptKey).toString();
            }
        } catch (Error: any) {
            // console.log("plainText : " , plainText);
            // console.log("encrypt() Error Message : " , Error.message);
        }
    }

    decrypt(cipherText: any) {
        try {
            return AES.decrypt(cipherText.trim(), this.EncryptKey).toString(CryptoTS.enc.Utf8);

        } catch (Error: any) {
            // console.log("cipherText : " + cipherText);
            // console.log("decrypt() Error Message : " + Error.message);
        }
    }

    //The set method is use for encrypt the value.
    set(keys: any, value: any) {
        var key = CryptoTS.enc.Utf8.parse(keys);
        var iv = CryptoTS.enc.Utf8.parse(keys);
        var encrypted = CryptoTS.AES.encrypt(CryptoTS.enc.Utf8.parse(value.toString()), key,
            {
                //keySize: 128 / 8,
                iv: iv,
                mode: CryptoTS.mode.CBC,
                padding: CryptoTS.pad.PKCS7
            });

        return encrypted.toString();
    }

    //The get method is use for decrypt the value.
    get(keys: any, value: any) {
        var keySize = 128 / 8
        var key = CryptoTS.enc.Utf8.parse(keys);
        var iv = CryptoTS.enc.Utf8.parse(keys);
        var decrypted = CryptoTS.AES.decrypt(value, key, {
            // keySize: keySize,
            iv: iv,
            mode: CryptoTS.mode.CBC,
            padding: CryptoTS.pad.PKCS7
        });

        return decrypted.toString(CryptoTS.enc.Utf8);
    }

    // Convenience: encrypt an object (returns AES-encrypted string of JSON)
    encryptObject(obj: any) {
        try {
            const str = JSON.stringify(obj);
            return this.encrypt(str);
        } catch (e) {
            return undefined;
        }
    }

    // Convenience: decrypt AES-encrypted JSON string into object
    decryptToObject(cipherText: any) {
        try {
            const dec = this.decrypt(cipherText);
            if (!dec && dec !== "") return undefined;
            return JSON.parse(dec);
        } catch (e) {
            return undefined;
        }
    }

    // --- Backend-compatible encryption helpers using Web Crypto ---
    // Format: Base64UrlEncode( IV (16 bytes) || CipherText ) where key = SHA256(secretKey)
    async encryptForBackend(obj: any, secretKey?: string) {
        try {
            const text = typeof obj === "string" ? obj : JSON.stringify(obj);
            const keyStr = secretKey || this.EncryptKey;
            const enc = new TextEncoder();
            const keyHash = await crypto.subtle.digest("SHA-256", enc.encode(keyStr));
            const cryptoKey = await crypto.subtle.importKey(
                "raw",
                keyHash,
                { name: "AES-CBC" },
                false,
                ["encrypt"]
            );
            const iv = crypto.getRandomValues(new Uint8Array(16));
            const cipher = await crypto.subtle.encrypt({ name: "AES-CBC", iv }, cryptoKey, enc.encode(text));
            const ivAndCipher = new Uint8Array(iv.byteLength + cipher.byteLength);
            ivAndCipher.set(iv, 0);
            ivAndCipher.set(new Uint8Array(cipher), iv.byteLength);
            return this.base64UrlEncode(ivAndCipher);
        } catch (e) {
            return undefined;
        }
    }

    async decryptFromBackend(token: string, secretKey?: string) {
        try {
            const keyStr = secretKey || this.EncryptKey;
            const enc = new TextEncoder();
            const keyHash = await crypto.subtle.digest("SHA-256", enc.encode(keyStr));
            const data = this.base64UrlDecodeToUint8(token);
            const iv = data.slice(0, 16);
            const cipher = data.slice(16);
            const cryptoKey = await crypto.subtle.importKey(
                "raw",
                keyHash,
                { name: "AES-CBC" },
                false,
                ["decrypt"]
            );
            const plainBuf = await crypto.subtle.decrypt({ name: "AES-CBC", iv }, cryptoKey, cipher);
            const dec = new TextDecoder().decode(plainBuf);
            return dec;
        } catch (e) {
            return undefined;
        }
    }

    // --- Server-format helpers matching backend `EncryptionService` ---
    // Backend uses AES-CBC with key = UTF8 bytes of EncryptKey.Substring(0,32)
    // and iv = UTF8 bytes of EncryptKey.Substring(0,16), and returns standard Base64 of ciphertext.
    async encryptServerFormat(obj: any, secretKey?: string) {
        try {
            const text = typeof obj === "string" ? obj : JSON.stringify(obj);
            const keyStr = (secretKey || this.EncryptKey) || "";
            const keyBytes = new TextEncoder().encode(keyStr.substring(0, 32));
            const ivBytes = new TextEncoder().encode(keyStr.substring(0, 16));
            const cryptoKey = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, ["encrypt"]);
            const cipher = await crypto.subtle.encrypt({ name: "AES-CBC", iv: ivBytes }, cryptoKey, new TextEncoder().encode(text));
            const cipherBytes = new Uint8Array(cipher);
            let binary = "";
            for (let i = 0; i < cipherBytes.length; i++) {
                binary += String.fromCharCode(cipherBytes[i]);
            }
            const b64 = btoa(binary);
            return b64;
        } catch (e) {
            return undefined;
        }
    }

    async decryptServerFormat(b64CipherText: string, secretKey?: string) {
        try {
            const keyStr = (secretKey || this.EncryptKey) || "";
            const keyBytes = new TextEncoder().encode(keyStr.substring(0, 32));
            const ivBytes = new TextEncoder().encode(keyStr.substring(0, 16));
            const binary = atob(b64CipherText);
            const len = binary.length;
            const cipherBytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) cipherBytes[i] = binary.charCodeAt(i);
            const cryptoKey = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, ["decrypt"]);
            const plainBuf = await crypto.subtle.decrypt({ name: "AES-CBC", iv: ivBytes }, cryptoKey, cipherBytes);
            return new TextDecoder().decode(plainBuf);
        } catch (e) {
            return undefined;
        }
    }

    private base64UrlEncode(bytes: Uint8Array) {
        let binary = "";
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }

    private base64UrlDecodeToUint8(base64url: string) {
        let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
        switch (base64.length % 4) {
            case 2: base64 += "=="; break;
            case 3: base64 += "="; break;
        }
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    }
}